import { IKimAddress, IKimAttachment } from '@alberta/konexi-shared';
import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { AlertController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { InfoBoxType } from 'src/app/shared/components/common/info-box/info-box.component';
import { IKimRecipient, IKimSendResult } from 'src/app/shared/models/kim/kim.model';
import { SelectionItem } from 'src/app/shared/models/selectionItem';
import { AuthService } from 'src/app/shared/services/auth.service';
import { KimApiService } from 'src/app/shared/services/kim/kim-api.service';
import { NetworkInfoService } from 'src/app/shared/services/network/network-info.service';

import { IKimRecipientSearchResult, KimRecipientSearchModal } from '../kim-recipient-search/kim-recipient-search.modal';

@Component({
  selector: 'itl-kim-send',
  templateUrl: './kim-send.modal.html',
  styleUrls: ['./kim-send.modal.scss'],
})
export class KimSendModal implements OnInit {
  @Input() attachment: IKimAttachment;
  @Input() subject: string;
  @Input() body: string;

  public senderControl = new UntypedFormControl(null, Validators.required);
  public recipientControl = new UntypedFormControl(null, Validators.required);
  public senderSelectionItems: SelectionItem[] = [];
  public noKimAddress = false;
  public vzdUnavailable = false;
  public sending = false;
  public infoBoxTypeWarning = InfoBoxType.Warning;

  public get recipient(): IKimRecipient | null {
    return this.recipientControl.value;
  }

  public get pageIsValid(): boolean {
    // ein disabled Control (genau eine vorbelegte Absenderadresse) ist in Angular nie "valid"
    const senderValid = this.senderControl.disabled ? Boolean(this.senderControl.value) : this.senderControl.valid;
    return !this.noKimAddress && !this.sending && senderValid && Boolean(this.recipient);
  }

  constructor(
    private _modalController: ModalController,
    private _alertController: AlertController,
    private _translateService: TranslateService,
    private _authService: AuthService,
    private _kimApiService: KimApiService,
    private _networkInfoService: NetworkInfoService
  ) {}

  async ngOnInit(): Promise<void> {
    const account = await this._authService.getAccount();
    const userKimAddresses = account?.kimAddresses ?? [];

    if (!userKimAddresses.length) {
      this.noKimAddress = true;
      return;
    }

    const organization = await this._authService.getCurrentUserOrganization();
    const organizationKimAddresses: IKimAddress[] = organization?.alberta?.kimAddresses ?? [];
    this.senderSelectionItems = userKimAddresses.map(kimAddress => {
      const organizationAddress = organizationKimAddresses.find(entry => entry.kimAddress === kimAddress);
      const label = organizationAddress ? `${kimAddress} (IK ${organizationAddress.ikNumber})` : kimAddress;
      return new SelectionItem(kimAddress, label);
    });

    if (userKimAddresses.length === 1) {
      this.senderControl.setValue(userKimAddresses[0]);
      this.senderControl.disable();
    }
  }

  public async openRecipientSearch(): Promise<void> {
    const modal = await this._modalController.create({
      component: KimRecipientSearchModal,
      backdropDismiss: false,
      cssClass: 'fullPageModal',
    });
    await modal.present();
    const result = await modal.onDidDismiss();
    const data = result.data as IKimRecipientSearchResult | undefined;
    if (data?.recipient) {
      this.recipientControl.setValue(data.recipient);
      this.vzdUnavailable = false;
    } else if (data?.vzdUnavailable && !this.recipient) {
      this.vzdUnavailable = true;
    }
  }

  public removeRecipient(event: Event): void {
    event.stopPropagation();
    this.recipientControl.setValue(null);
    this.recipientControl.markAsTouched();
  }

  public async retryVzd(): Promise<void> {
    this.vzdUnavailable = false;
    await this.openRecipientSearch();
  }

  public async cancel(): Promise<void> {
    await this._modalController.dismiss().catch(error => console.log(error));
  }

  public async send(): Promise<void> {
    if (!this.pageIsValid) {
      this.senderControl.markAsTouched();
      this.recipientControl.markAsTouched();
      return;
    }

    if (this._networkInfoService.isNotConnected || navigator.onLine === false) {
      await this.showRetryAlert('kim.send.offline.header', 'kim.send.offline.message');
      return;
    }

    this.sending = true;
    try {
      const sender: string = this.senderControl.value;
      await this._kimApiService.send({
        from: sender,
        to: this.recipient.kimAddress,
        subject: this.subject,
        body: this.body ?? this._translateService.instant('kim.send.defaultBody'),
        attachments: this.attachment ? [this.attachment] : undefined,
      });
      const sendResult: IKimSendResult = {
        sent: true,
        sender,
        recipient: this.recipient,
        sentAt: new Date(),
      };
      await this._modalController.dismiss(sendResult);
    } catch (error) {
      window.logger.error('KIM send failed', error);
      this.sending = false;
      await this.showRetryAlert('kim.send.error.header', 'kim.send.error.message');
    } finally {
      this.sending = false;
    }
  }

  private async showRetryAlert(headerKey: string, messageKey: string): Promise<void> {
    const alert = await this._alertController.create({
      header: this._translateService.instant(headerKey),
      message: this._translateService.instant(messageKey),
      buttons: [
        { text: this._translateService.instant('kim.send.close'), role: 'cancel' },
        { text: this._translateService.instant('kim.send.retry'), role: 'retry' },
      ],
      backdropDismiss: false,
    });
    await alert.present();
    const result = await alert.onDidDismiss();
    if (result.role === 'retry') {
      await this.send();
    }
  }
}
